import type { Ref } from "react";
import { tv } from "tailwind-variants";

import { icons } from "./icons";
import { focusRing } from "./interaction";

const counterVariants = tv({
  slots: {
    container: "flex w-fit items-center justify-between gap-3",
    button: [
      "cursor-pointer rounded-full border border-line-strong p-2.5 text-content transition-colors",
      "hover:border-focus hover:bg-surface-hover hover:text-accent",
      focusRing,
      "disabled:cursor-not-allowed disabled:text-content-subtle disabled:hover:border-line-strong disabled:hover:bg-transparent disabled:hover:text-content-subtle",
    ],
  },
  variants: {
    disabled: {
      true: {
        container: "opacity-50",
      },
    },
  },
});

const { button, container } = counterVariants();

type CounterProps = {
  value: number;
  onChange: (value: number) => void;
  min?: number;
  max?: number;
  disabled?: boolean;
  className?: string;
  /**
    What the counter counts, for screen readers: "Decrease Daily Balance quantity, currently 1".
    Needed wherever several counters sit in one list; without it the buttons say "value".
  */
  label?: string;
  /** Lets a parent put focus back on a button, for example after the row re-renders. */
  decreaseRef?: Ref<HTMLButtonElement>;
  increaseRef?: Ref<HTMLButtonElement>;
};

const getButtonLabel = (action: "Decrease" | "Increase", value: number, label?: string) => {
  return label
    ? `${action} ${label} quantity, currently ${value}`
    : `${action} value, current value is ${value}`;
};

export const Counter = ({
  className,
  decreaseRef,
  disabled = false,
  increaseRef,
  label,
  max = Infinity,
  min = 0,
  onChange,
  value,
}: CounterProps) => {
  const handleDecrease = () => {
    const nextValue = Math.max(value - 1, min);
    if (nextValue !== value) {
      onChange(nextValue);
    }
  };

  const handleIncrease = () => {
    const nextValue = Math.min(value + 1, max);
    if (nextValue !== value) {
      onChange(nextValue);
    }
  };

  const isAtMin = value <= min;
  const isAtMax = value >= max;

  return (
    <div className={container({ className, disabled })}>
      <button
        aria-label={getButtonLabel("Decrease", value, label)}
        className={button()}
        disabled={disabled || isAtMin}
        onClick={handleDecrease}
        ref={decreaseRef}
        type="button"
      >
        <icons.MinusIcon />
      </button>

      <span className="text-center font-semibold">{value}</span>

      <button
        aria-label={getButtonLabel("Increase", value, label)}
        className={button()}
        disabled={disabled || isAtMax}
        onClick={handleIncrease}
        ref={increaseRef}
        type="button"
      >
        <icons.PlusIcon />
      </button>
    </div>
  );
};
